import { FocusEvent } from "react";
import {
    Table, TableContainer, TableHead, TableBody, TableRow, TableCell,
    TextField, MenuItem, Typography, Box
} from "@mui/material";
import { IPayrollShuttleEntry } from "../../interface/payrollShuttle";
import { CONTRACT_NATURE_OPTIONS, EMPLOYEE_STATUS_OPTIONS } from "../../constants/payrollShuttle";

export type EditablePayrollField =
    | "matricule"
    | "contractNature"
    | "employeeStatus"
    | "hoursNormalized"
    | "transportReimbursement"
    | "transportBonus"
    | "kilometricAllowance"
    | "professionalExpenses"
    | "disciplinaryLayoffStart"
    | "disciplinaryLayoffEnd"
    | "conservatoryLayoffStart"
    | "conservatoryLayoffEnd";

interface PayrollShuttleEntryTableProps {
    rows: IPayrollShuttleEntry[];
    onFieldChange: (id: string, field: EditablePayrollField, value: string | number) => void;
}

const numberCellSx = { width: 110 };

/** Sélectionne tout le contenu au clic, pour qu'un 0 par défaut ne gêne pas la saisie d'une nouvelle valeur. */
const selectOnFocus = (e: FocusEvent<HTMLInputElement>) => e.target.select();

const dateRowSx = { display: "flex", alignItems: "center", gap: 0.5 };

const NOM_WIDTH = 130;
const PRENOM_WIDTH = 110;

/** Colonne figée (Nom/Prénom) dans le corps du tableau : reste visible au scroll horizontal. */
const stickyLeftSx = (left: number, width: number) => ({
    position: "sticky" as const,
    left,
    width,
    minWidth: width,
    maxWidth: width,
    zIndex: 1,
    bgcolor: "background.paper",
    boxShadow: "2px 0 4px -2px rgba(0,0,0,0.15)",
});

/** Ligne d'en-tête figée : reste visible au scroll vertical. */
const stickyTopSx = {
    position: "sticky" as const,
    top: 0,
    zIndex: 2,
    bgcolor: "background.paper",
};

/** Cellules d'en-tête Nom/Prénom : figées à la fois horizontalement et verticalement. */
const stickyTopLeftSx = (left: number, width: number) => ({
    ...stickyLeftSx(left, width),
    ...stickyTopSx,
    zIndex: 3,
});

export default function PayrollShuttleEntryTable({ rows, onFieldChange }: PayrollShuttleEntryTableProps) {
    if (rows.length === 0) {
        return (
            <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography color="text.secondary">
                    Aucune donnée pour ce mois. Cliquez sur "Générer" pour calculer la navette de paie.
                </Typography>
            </Box>
        );
    }

    return (
        <TableContainer sx={{ maxHeight: "70vh", overflow: "auto" }}>
            <Table size="small" stickyHeader sx={{ minWidth: 1350 }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={stickyTopLeftSx(0, NOM_WIDTH)}>Nom</TableCell>
                        <TableCell sx={stickyTopLeftSx(NOM_WIDTH, PRENOM_WIDTH)}>Prénom</TableCell>
                        <TableCell sx={stickyTopSx}>Matricule</TableCell>
                        <TableCell sx={stickyTopSx}>Nature du contrat</TableCell>
                        <TableCell sx={stickyTopSx}>Statut</TableCell>
                        <TableCell sx={stickyTopSx}>Heures normales mensualisées</TableCell>
                        <TableCell sx={stickyTopSx}>Remb. transport</TableCell>
                        <TableCell sx={stickyTopSx}>Prime transport</TableCell>
                        <TableCell sx={stickyTopSx}>Indemnité km</TableCell>
                        <TableCell sx={stickyTopSx}>Frais professionnels</TableCell>
                        <TableCell sx={stickyTopSx}>Mise à pied disciplinaire</TableCell>
                        <TableCell sx={stickyTopSx}>Mise à pied conservatoire</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow key={row._id}>
                            <TableCell sx={stickyLeftSx(0, NOM_WIDTH)}>{row.lastname}</TableCell>
                            <TableCell sx={stickyLeftSx(NOM_WIDTH, PRENOM_WIDTH)}>{row.firstname}</TableCell>
                            <TableCell>
                                <TextField
                                    variant="standard"
                                    value={row.matricule || ""}
                                    onChange={(e) => onFieldChange(row._id, "matricule", e.target.value)}
                                />
                            </TableCell>
                            <TableCell>
                                <TextField
                                    select
                                    variant="standard"
                                    sx={{ width: 130 }}
                                    value={row.contractNature || ""}
                                    onChange={(e) => onFieldChange(row._id, "contractNature", e.target.value)}
                                >
                                    <MenuItem value="">—</MenuItem>
                                    {CONTRACT_NATURE_OPTIONS.map((option) => (
                                        <MenuItem key={option} value={option}>{option}</MenuItem>
                                    ))}
                                </TextField>
                            </TableCell>
                            <TableCell>
                                <TextField
                                    select
                                    variant="standard"
                                    sx={{ width: 150 }}
                                    value={row.employeeStatus || ""}
                                    onChange={(e) => onFieldChange(row._id, "employeeStatus", e.target.value)}
                                >
                                    <MenuItem value="">—</MenuItem>
                                    {EMPLOYEE_STATUS_OPTIONS.map((option) => (
                                        <MenuItem key={option} value={option}>{option}</MenuItem>
                                    ))}
                                </TextField>
                            </TableCell>
                            <TableCell>
                                <TextField
                                    type="number"
                                    variant="standard"
                                    sx={numberCellSx}
                                    value={row.hoursNormalized ?? 0}
                                    onChange={(e) => onFieldChange(row._id, "hoursNormalized", Number(e.target.value))}
                                    onFocus={selectOnFocus}
                                />
                            </TableCell>
                            <TableCell>
                                <TextField
                                    type="number"
                                    variant="standard"
                                    sx={numberCellSx}
                                    value={row.transportReimbursement ?? 0}
                                    onChange={(e) => onFieldChange(row._id, "transportReimbursement", Number(e.target.value))}
                                    onFocus={selectOnFocus}
                                />
                            </TableCell>
                            <TableCell>
                                <TextField
                                    type="number"
                                    variant="standard"
                                    sx={numberCellSx}
                                    value={row.transportBonus ?? 0}
                                    onChange={(e) => onFieldChange(row._id, "transportBonus", Number(e.target.value))}
                                    onFocus={selectOnFocus}
                                />
                            </TableCell>
                            <TableCell>
                                <TextField
                                    type="number"
                                    variant="standard"
                                    sx={numberCellSx}
                                    value={row.kilometricAllowance ?? 0}
                                    onChange={(e) => onFieldChange(row._id, "kilometricAllowance", Number(e.target.value))}
                                    onFocus={selectOnFocus}
                                />
                            </TableCell>
                            <TableCell>
                                <TextField
                                    type="number"
                                    variant="standard"
                                    sx={numberCellSx}
                                    value={row.professionalExpenses ?? 0}
                                    onChange={(e) => onFieldChange(row._id, "professionalExpenses", Number(e.target.value))}
                                    onFocus={selectOnFocus}
                                />
                            </TableCell>
                            <TableCell>
                                <Box sx={dateRowSx}>
                                    <Typography variant="caption" color="text.secondary">Du :</Typography>
                                    <TextField
                                        type="date"
                                        variant="standard"
                                        InputLabelProps={{ shrink: true }}
                                        value={row.disciplinaryLayoffStart || ""}
                                        onChange={(e) => onFieldChange(row._id, "disciplinaryLayoffStart", e.target.value)}
                                    />
                                </Box>
                                <Box sx={dateRowSx}>
                                    <Typography variant="caption" color="text.secondary">au :</Typography>
                                    <TextField
                                        type="date"
                                        variant="standard"
                                        InputLabelProps={{ shrink: true }}
                                        value={row.disciplinaryLayoffEnd || ""}
                                        onChange={(e) => onFieldChange(row._id, "disciplinaryLayoffEnd", e.target.value)}
                                    />
                                </Box>
                            </TableCell>
                            <TableCell>
                                <Box sx={dateRowSx}>
                                    <Typography variant="caption" color="text.secondary">Du :</Typography>
                                    <TextField
                                        type="date"
                                        variant="standard"
                                        InputLabelProps={{ shrink: true }}
                                        value={row.conservatoryLayoffStart || ""}
                                        onChange={(e) => onFieldChange(row._id, "conservatoryLayoffStart", e.target.value)}
                                    />
                                </Box>
                                <Box sx={dateRowSx}>
                                    <Typography variant="caption" color="text.secondary">au :</Typography>
                                    <TextField
                                        type="date"
                                        variant="standard"
                                        InputLabelProps={{ shrink: true }}
                                        value={row.conservatoryLayoffEnd || ""}
                                        onChange={(e) => onFieldChange(row._id, "conservatoryLayoffEnd", e.target.value)}
                                    />
                                </Box>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
